import { useCallback } from 'react';
import {
  XYPosition,
  NodeChange,
  applyNodeChanges,
  NodePositionChange,
  EdgeChange,
  applyEdgeChanges,
  useReactFlow,
  Node,
} from '@xyflow/react';
import { CanvasElementType, CanvasEventType } from '../canvas-types';
import {
  CanvasGridPosition,
  convertXYPositionToGridIndex,
  snapPositionToGrid,
} from '../helpers/gridPositioningUtils';
import { RfNodeStickyNote, RfNodeTextNode, SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import { RFEdgeFloatingEdge } from '../edges';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import { handleDimensionChange } from './handleDimensionChange';
import { useGetNodeAbsolutePosition } from '../helpers/useGetNodeAbsolutePosition';
import { useOnNodePositionChanges } from './position-events/useOnNodePositionChanges';
import { useSelectNodes } from './useSelectNodes';
import { useNodeTextScaleCtx } from '../internal-context/NodeTextScaleContext';

export type NodeTransformations = {
  extraNodePositionChanges: NodePositionChange[];
};

export interface MovingNodesOriginalPositionRef {
  [nodeId: string]: { originalPosition: XYPosition };
}

const isTemporaryNode = (node: Node) => {
  return node.id.startsWith('temp-');
};

const MIN_ZOOM_TO_FIT_NEW_NODE = 0.6;

export const useOnNodeChange = () => {
  const { setCanvasNodes: setNodes, setCanvasEdges: setEdges } = useSweepCanvasState();
  const { getNode: getReactFlowNode } = useReactFlow();
  const { getNodeAbsolutePosition } = useGetNodeAbsolutePosition();
  const { handlePositionChanges } = useOnNodePositionChanges();
  const selectNodes = useSelectNodes();
  const { fitView, getViewport } = useReactFlow();

  const { onSweepNodesChange } = useSweepCanvasPropsCtx();
  const { nodesTextScaleRef } = useNodeTextScaleCtx();
  const onNodesChange = useCallback(
    (nodeChanges: NodeChange<SweepCanvasRfNode>[]) => {
      setNodes((nodes, nodeOperations) => {
        const removeChange = nodeChanges.find((nodeChange) => nodeChange.type === 'remove');
        if (removeChange) {
          const node = getReactFlowNode(removeChange.id);
          if (node && !isTemporaryNode(node)) {
            if (node.type === CanvasElementType.SECTION) {
              // Find all child nodes and prepare their new positions
              const nodesToMove: Array<{
                nodeId: string;
                newPosition: CanvasGridPosition;
                xyPosition: XYPosition;
                type: string;
              }> = [];

              nodes.forEach((childNode) => {
                if (childNode.parentId === removeChange.id) {
                  // Calculate absolute position
                  const absolutePosition = getNodeAbsolutePosition(childNode.id);

                  if (absolutePosition) {
                    // Apply grid snapping only for groups and standalone nodes
                    const shouldSnapToGrid =
                      childNode.type === CanvasElementType.GROUP ||
                      childNode.type === CanvasElementType.STANDALONE_NODE;
                    const finalPosition = shouldSnapToGrid
                      ? snapPositionToGrid(absolutePosition, {
                          withPaddingByNodeType: childNode.type,
                        })
                      : absolutePosition;

                    // Convert to grid position
                    const gridPosition = convertXYPositionToGridIndex(finalPosition, {
                      withPaddingByNodeType: childNode.type,
                    });

                    nodesToMove.push({
                      nodeId: childNode.id,
                      newPosition: gridPosition,
                      xyPosition: finalPosition,
                      type: childNode.type,
                    });
                  }
                }
              });

              onSweepNodesChange?.({
                type: CanvasEventType.UngroupSection,
                id: removeChange.id,
                nodesToMove,
              });
              return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
            }
            if (node.type === CanvasElementType.STICKY_NOTE) {
              onSweepNodesChange?.({
                type: CanvasEventType.RemoveStickyNote,
                id: removeChange.id,
              });
              return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
            }
            if (node.type === CanvasElementType.TEXT_NODE) {
              onSweepNodesChange?.({
                type: CanvasEventType.RemoveTextNode,
                id: removeChange.id,
              });
              return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
            }
          }
        }

        const dimensionChangeResult = handleDimensionChange(
          nodeChanges,
          nodeOperations,
          nodesTextScaleRef,
        );
        const isNodeDragging = nodeChanges.some(
          (nodeChange) => nodeChange.type === 'position' && nodeChange.dragging,
        );

        if (dimensionChangeResult.hasDimensionChange && !isNodeDragging) {
          if (dimensionChangeResult.canvasEvent) {
            onSweepNodesChange?.(dimensionChangeResult.canvasEvent);
          }
          return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
        }

        const addChange = nodeChanges.find((nodeChange) => nodeChange.type === 'add');
        if (addChange) {
          if (addChange.item.type === CanvasElementType.SECTION) {
            const item = addChange.item;
            addChange.item.data.tempChildNodes?.forEach(({ id, xyPosition }) => {
              nodeOperations.setObject(id, (node) => ({
                ...node,
                position: xyPosition,
                parentId: item.id,
              }));
            });

            onSweepNodesChange?.({
              type: CanvasEventType.AddSection,
              id: item.id,
              xyPosition: item.position,
              color: item.data.color,
              name: item.data.name,
              width: item.width ?? 0,
              height: item.height ?? 0,
              nodesToMove: item.data.tempChildNodes,
            });
            setTimeout(() => {
              selectNodes([item.id]);
            }, 100);
            return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
          }
          if (addChange.item.type === CanvasElementType.STICKY_NOTE) {
            if (!addChange.item.data.draft) {
              onSweepNodesChange?.({
                type: CanvasEventType.AddStickyNote,
                id: addChange.item.id,
                xyPosition: addChange.item.position,
                backgroundColor: addChange.item.data.color,
                richText: addChange.item.data.richText,
                width: addChange.item.width ?? 120,
                height: addChange.item.height ?? 120,
                parentId: addChange.item.parentId,
                textScale: addChange.item.data.textScale ?? 1,
              });
              setTimeout(() => {
                selectNodes([addChange.item.id]);
              }, 100);
              if (getViewport().zoom < MIN_ZOOM_TO_FIT_NEW_NODE) {
                fitView({
                  nodes: [addChange.item],
                  duration: 300,
                  maxZoom: 1,
                });
              }
            }
          }
          if (addChange.item.type === CanvasElementType.TEXT_NODE) {
            if (getViewport().zoom < MIN_ZOOM_TO_FIT_NEW_NODE) {
              fitView({
                nodes: [addChange.item],
                duration: 300,
                maxZoom: 1,
              });
            }
          }

          return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
        }
        const replaceChange = nodeChanges.find((nodeChange) => nodeChange.type === 'replace');

        if (replaceChange) {
          switch (replaceChange.item.type) {
            case CanvasElementType.SECTION:
              onSweepNodesChange?.({
                type: CanvasEventType.SectionChangeData,
                id: replaceChange.item.id,
                color: replaceChange.item.data.color,
                name: replaceChange.item.data.name,
              });
              break;
            case CanvasElementType.STICKY_NOTE: {
              if (!replaceChange.item.data.preventCanvasEvent) {
                onSweepNodesChange?.({
                  type: CanvasEventType.ChangeStickyNoteData,
                  id: replaceChange.item.id,
                  color: replaceChange.item.data.color,
                  richText: replaceChange.item.data.richText,
                  height: Math.round(replaceChange.item.height ?? 0),
                  textScale: replaceChange.item.data.textScale,
                });
                nodeOperations.setObject(replaceChange.item.id, (node: RfNodeStickyNote) => {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      preventCanvasEvent: false,
                    },
                  };
                });
              }
              break;
            }
            case CanvasElementType.GROUP:
              onSweepNodesChange?.({
                type: CanvasEventType.MoveGroup,
                nodeId: replaceChange.item.id,
                newPosition: convertXYPositionToGridIndex(replaceChange.item.position, {
                  withPaddingByNodeType: replaceChange.item.type,
                }),
                parentId: replaceChange.item.parentId,
              });
              break;
            case CanvasElementType.STANDALONE_NODE:
              onSweepNodesChange?.({
                type: CanvasEventType.MoveStandaloneNode,
                nodeId: replaceChange.item.id,
                newPosition: convertXYPositionToGridIndex(replaceChange.item.position, {
                  withPaddingByNodeType: replaceChange.item.type,
                }),
              });
              break;
            case CanvasElementType.TEXT_NODE:
              if (replaceChange.item.data.richText && !replaceChange.item.data.freshlyCreated) {
                selectNodes([]);
              }

              if (replaceChange.item.data.temporary) {
                onSweepNodesChange?.({
                  type: CanvasEventType.AddTextNode,
                  id: replaceChange.item.id,
                  richText: replaceChange.item.data.richText,
                  textScale: replaceChange.item.data.textScale,
                  height: Math.round(replaceChange.item.height ?? 0),
                  xyPosition: replaceChange.item.position,
                  width: Math.round(replaceChange.item.width ?? 0),
                  parentId: replaceChange.item.parentId,
                });
                nodeOperations.setObject(replaceChange.item.id, (node: RfNodeTextNode) => {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      temporary: false,
                      freshlyCreated: false,
                    },
                  };
                });
              } else {
                onSweepNodesChange?.({
                  type: CanvasEventType.ChangeTextNodeData,
                  id: replaceChange.item.id,
                  richText: replaceChange.item.data.richText,
                  textScale: replaceChange.item.data.textScale,
                  height: Math.round(replaceChange.item.height ?? 0),
                });
              }

              break;
            default:
              break;
          }
          return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
        }
        const positionChanges = nodeChanges.filter((nodeChange) => nodeChange.type === 'position');
        if (positionChanges.length) {
          const extraNodePositionChanges = handlePositionChanges({
            positionChanges,
            nodeOperations,
            hasDimensionChange: dimensionChangeResult.hasDimensionChange,
          });

          return applyNodeChanges(
            [...nodeChanges, ...extraNodePositionChanges],
            nodeOperations.getObjects(),
          );
        }

        return applyNodeChanges(nodeChanges, nodeOperations.getObjects());
      });
    },
    [
      setNodes,
      nodesTextScaleRef,
      getReactFlowNode,
      onSweepNodesChange,
      getNodeAbsolutePosition,
      selectNodes,
      getViewport,
      fitView,
      handlePositionChanges,
    ],
  );

  const onEdgesChange = useCallback(
    (edgeChanges: EdgeChange<RFEdgeFloatingEdge>[]) => {
      setEdges((edges) => applyEdgeChanges(edgeChanges, edges));
    },
    [setEdges],
  );

  return {
    onNodesChange,
    onEdgesChange,
  };
};
